/**
 * "Onay Bekleyenler" ekrani - YALNIZCA YONETICI (kullanici istegi).
 *
 * NE GOSTERIR
 *   UT Bot'un BUY/SELL verdigi ama HENUZ ONAYLANMAMIS adaylar. Iki
 *   asamali onay (bkz. strategy/signals.py):
 *
 *     bar N    UT Bot BUY verdi        -> ADAY (bu ekran)
 *     bar N+1  UT pozisyonu hala BUY?  -> EVET: ONAYLI SINYAL
 *
 * NEDEN AYRI BIR EKRAN
 *   Bu satirlar sinyal DEGILDIR ve Tarama ekranina karistirilmamalidir.
 *   Onay barini beklemeden girmek bu projede olculdu ve REDDEDILDI
 *   (research/REPORT_sinyalx_4h_erken_giris.md): onayli GIRIS onaysizdan
 *   daha iyidir. Ekran "hazirlik / izleme" icindir, "simdi al" demek
 *   degildir - kart ustundeki uyari bunu acikca soyler.
 *
 * KALITE ONIZLEMEDIR
 *   Urun kaliteyi ONAY aninda hesaplar; adayda henuz yoktur. Buradaki
 *   skor o ana kadarki barlarla hesaplanir ve onay aninda DEGISEBILIR.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { PendingCandidate, PendingScan } from "../api/types";
import { dateTime, price } from "../lib/format";
import { TimeframeChips } from "./SignalScreen";
import { Banner, IconRefresh, Kpi, Pill } from "./Ui";

/** Aday sayisi az degisir; taramadan daha seyrek yoklanir. */
const POLL_MS = 30_000;

function countdown(targetMs: number, nowMs: number): string {
  const left = Math.max(0, targetMs - nowMs);
  const hours = Math.floor(left / 3_600_000);
  const minutes = Math.floor((left % 3_600_000) / 60_000);
  if (hours > 0) return `${hours} sa ${minutes} dk`;
  return `${minutes} dk`;
}

function PendingSection({
  title,
  rows,
  emptyHint,
  note,
  onSelect,
}: {
  title: string;
  rows: PendingCandidate[];
  emptyHint: string;
  note?: string;
  onSelect: (symbol: string) => void;
}) {
  return (
    <section className="pending-section">
      <div className="title-row">
        <h3 className="section-title">
          {title} <span className="pending-count">{rows.length}</span>
        </h3>
      </div>
      {note ? <p className="note pending-section-note">{note}</p> : null}
      {rows.length === 0 ? (
        <p className="note">{emptyHint}</p>
      ) : (
        <div className="rows">
          {rows.map((row) => (
            <button
              type="button"
              className="pending-row"
              key={`${row.symbol}-${row.event_close_time}`}
              data-side={row.side === "BUY" ? "up" : "down"}
              onClick={() => onSelect(row.symbol)}
            >
              <div className="pending-main">
                <span className="pending-symbol">{row.symbol}</span>
                {row.quality_summary_tr ? (
                  <Pill tone={(row.quality_score ?? 0) >= 4 ? "up" : "neutral"}>
                    Kalite {row.quality_summary_tr}
                  </Pill>
                ) : null}
              </div>
              <div className="pending-meta">
                <span className="muted">
                  Olay barı {dateTime(row.event_close_time)} · 24s hacim{" "}
                  {(row.quote_volume_24h / 1_000_000).toFixed(1)}M USDT
                </span>
              </div>
              <div className="pending-price">{price(row.price)}</div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export function PendingScreen({
  timeframe,
  timeframes,
  onTimeframeChange,
  onSelect,
}: {
  timeframe: string;
  timeframes: string[];
  onTimeframeChange: (next: string) => void;
  onSelect: (symbol: string) => void;
}) {
  const [data, setData] = useState<PendingScan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async (frame: string) => {
    setLoading(true);
    try {
      setData(await api.pendingSignals(frame));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Adaylar alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(timeframe);
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load(timeframe);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load, timeframe]);

  // Geri sayim her dakika tazelenir - istek YAPILMAZ.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const rows = data?.rows ?? [];
  const buys = useMemo(() => rows.filter((row) => row.side === "BUY"), [rows]);
  const sells = useMemo(() => rows.filter((row) => row.side === "SELL"), [rows]);

  return (
    <>
      <div className="title-row">
        <h2 className="section-title">Onay Bekleyenler</h2>
        <button
          type="button"
          className="icon-btn"
          onClick={() => void load(timeframe)}
          aria-label="Yenile"
          disabled={loading}
        >
          <IconRefresh spinning={loading} />
        </button>
      </div>

      <TimeframeChips timeframes={timeframes} value={timeframe} onChange={onTimeframeChange} />

      <div className="status-line">
        <span>{timeframe.toUpperCase()} · onay bekleyen adaylar</span>
        {data ? <span>{data.scanned} sembol tarandı</span> : null}
      </div>

      <Banner>
        Bunlar <strong>sinyal değildir</strong>. UT Bot bu barda yön verdi; sinyale
        dönüşmesi için <strong>sıradaki bar kapanışında da aynı yönde kalması</strong>{" "}
        gerekir. Yön değişirse aday sessizce iptal olur. Onay beklemeden girmek bu projede
        ölçüldü ve <strong>reddedildi</strong> — bu ekran hazırlık içindir, alım önerisi
        değildir.
      </Banner>

      {data ? (
        <div className="kpi-grid">
          <Kpi label="Toplam Aday" value={String(rows.length)} />
          <Kpi
            label="Yükseliş Adayı"
            value={String(buys.length)}
            tone={buys.length > 0 ? "up" : undefined}
          />
          <Kpi
            label="Düşüş Adayı"
            value={String(sells.length)}
            tone={sells.length > 0 ? "down" : undefined}
          />
          <Kpi
            label="Sıradaki Kapanış"
            value={countdown(data.next_close_time, now)}
            sub={dateTime(data.next_close_time)}
          />
        </div>
      ) : null}

      {error ? <Banner tone="error">{error}</Banner> : null}

      {/* YUKSELIS ONCE: kullanici islemi Yukselis ile aciyor, Dusus'u
          yalnizca ELINDEKINI nerede satacagini gormek icin kullaniyor.
          Dusus adaylari cogu zaman cok daha kalabalik oldugu icin tek
          listede birkac Yukselis adayi arada KAYBOLUYORDU. */}
      {data ? (
        <>
          <PendingSection
            title="Yükseliş Adayları"
            rows={buys}
            emptyHint={`${timeframe.toUpperCase()} zaman diliminde onay bekleyen Yükseliş adayı yok.`}
            onSelect={onSelect}
          />
          <PendingSection
            title="Düşüş Adayları"
            rows={sells}
            emptyHint={`${timeframe.toUpperCase()} zaman diliminde onay bekleyen Düşüş adayı yok.`}
            note="Düşüş adayları çıkış içindir: elinizde o coin varsa nerede satacağınızı gösterir."
            onSelect={onSelect}
          />
        </>
      ) : null}

      <p className="note" style={{ marginTop: 12 }}>
        <strong>Kalite bir önizlemedir.</strong> Ürün kaliteyi onay anında hesaplar; buradaki
        skor şu ana kadarki mumlarla hesaplandı ve onayda farklı çıkabilir.
      </p>
    </>
  );
}
